<template>
  <v-bottom-navigation
    v-model="activeValue"
    grow
    class="bottom-nav"
    color="primary"
  >
    <v-btn
      v-for="item in resolvedNavItems"
      :key="item.value"
      :value="item.routeName || item.value"
      @click="handleRedirect(item)"
      :exact="true"
    >
      <v-icon :icon="item.icon" />
      <span>{{ item.label }}</span>
    </v-btn>
  </v-bottom-navigation>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getActiveContext, getContextKind } from '@/modules/auth/services/auth.api'
import { getAuthToken } from "@/modules/auth/services/auth.storage";
import { isVendorMode } from "@/shared/utils/appMode";

const router = useRouter()
const route = useRoute()

const loggedOutItems = [
  { value: "home", label: "Home", icon: "mdi-home-variant-outline", routeName: "marketplace.home" },
  { value: "about", label: "Overview", icon: "mdi-information-outline", routeName: "marketplace.about" },
  { value: "contact", label: "Contact", icon: "mdi-phone-outline", routeName: "marketplace.contact" },
  { value: "login", label: "Login", icon: "mdi-login-variant", routeName: "login.page" },
];

const customerItems = [
  { value: "home", label: "Home", icon: "mdi-home-variant-outline", routeName: "marketplace.home" },
  { value: "dashboard", label: "Dashboard", icon: "mdi-chart-line", routeName: "customer.dashboard" },
  { value: "message", label: "Message", icon: "mdi-message-outline", routeName: "customer.messages" },
  { value: "settings", label: "Settings", icon: "mdi-cog-outline", routeName: "customer.settings" },
];


const companyItems = [
  { value: "home", label: "Home", icon: "mdi-home-variant-outline", routeName: "marketplace.home" },
  { value: "dashboard", label: "Dashboard", icon: "mdi-view-dashboard-outline", routeName: "company.dashboard" },
  { value: "message", label: "Message", icon: "mdi-message-outline", routeName: "company.messages" },
  { value: "settings", label: "Settings", icon: "mdi-cog-outline", routeName: "company.settings" },
];

const vendorItems = [
  { value: "home", label: "Home", icon: "mdi-home-variant-outline", routeName: "marketplace.home" },
  { value: "dashboard", label: "Dashboard", icon: "mdi-chart-line", routeName: "vendor.dashboard" },
  { value: "messages", label: "Messages", icon: "mdi-message-outline", routeName: "vendor.messages" },
  { value: "settings", label: "Settings", icon: "mdi-cog-outline", routeName: "vendor.settings" },
];

const activeValue = ref((route.name || '').toString())
const isAuthed = ref(Boolean(getAuthToken()))
const activeRole = ref(getContextKind(getActiveContext()))

const navItems = computed(() => {
  if (!isAuthed.value) return loggedOutItems

  if (isVendorMode()) {
    return vendorItems
  }
  switch (activeRole.value) {
    case "customer":
      return customerItems
    case "company":
      return companyItems
    case "vendor":
      return vendorItems
    default:
      return customerItems
  }
})

// filter out items whose named route does not exist (prevents "No match for" errors)
const resolvedNavItems = computed(() => {
  return navItems.value.filter((item) => {
    if (!item.routeName) return true
    try {
      return router.hasRoute(item.routeName)
    } catch {
      return false
    }
  })
})

watch(
  () => route.name,
  (name) => {
    activeValue.value = (name || '').toString()
  }
)

function handleRedirect(item) {

  if (item.routeName && router.hasRoute(item.routeName)) {
    router.push({ name: item.routeName })
  }
}

function syncAuthAndContext() {
  isAuthed.value = Boolean(getAuthToken())
  activeRole.value = getContextKind(getActiveContext())
}

function onStorage(e: StorageEvent) {
  if (!e.key || ["auth_token", "auth_active_context", "auth_contexts"].includes(e.key)) {
    syncAuthAndContext()
  }
}

onMounted(() => {
  window.addEventListener("auth-changed", syncAuthAndContext)
  window.addEventListener("storage", onStorage)
  syncAuthAndContext()
})

onBeforeUnmount(() => {
  window.removeEventListener("auth-changed", syncAuthAndContext)
  window.removeEventListener("storage", onStorage)
})
</script>

<style scoped>
.bottom-nav {
  background: #fff;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
}
:global(.v-bottom-navigation) {
  height: unset !important;
}
:global(.bottom-nav .v-bottom-navigation__content) {
  height: 74px;
  /* padding-bottom: 10px !important; */
  font-size: 0.85rem !important;
}

:global(.bottom-nav .v-btn) {
  font-size: 14px;
}

:global(.bottom-nav .v-icon) {
  font-size: 22px;
}
.v-bottom-navigation .v-bottom-navigation__content > .v-btn {
    padding-bottom: 18px !important;
}
</style>
